import { useRef, useState } from 'react';
import axios from 'axios';
import { urlPreviewUtils } from './urlPreview';
import { fileAttachmentUtils } from './fileAttachmentUtils';

/**
 * Shared utility functions for email template management
 * Used by both EmailTemplates component and CampaignModal
 */
export const templateUtils = {
  utcToTimeZone(utcString, timeZone, isDate = true) {
    utcString = utcString.endsWith('Z') ? utcString : utcString + 'Z';
    const utcDate = new Date(utcString);
    const tzString = utcDate.toLocaleString("en-CA", { timeZone }); // en-CA gives YYYY-MM-DD format
    if (isDate) {
      return tzString.split(',')[0]; // only take date part
    }

    return tzString

  },

  dateToUTC(dateValue, timeZone, startTime) {
    // Create the date string
    const dateStr = `${dateValue}T${startTime}:00`;

    // Parse it in the target timezone to get UTC equivalent
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hourCycle: 'h23',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    }).formatToParts(new Date(dateStr));

    const get = (type) => parts.find(p => p.type === type).value;

    // Build ISO string assuming these parts are in the timezone
    const localDate = new Date(`${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}:${get('second')}`);

    // Calculate offset and adjust
    const utcDate = new Date(dateStr);
    const offset = localDate - utcDate;

    return new Date(utcDate - offset).toISOString().slice(0, 19);
  },

  utcToLocaleDate(utcString) {
    utcString = utcString.endsWith('Z') ? utcString : utcString + 'Z';
    const date = new Date(utcString);
    return date.toLocaleDateString();
  },

  utcToLocalInput(utcString, hasT = true) {
    utcString = utcString.endsWith('Z') ? utcString : utcString + 'Z';
    const date = new Date(utcString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    if (hasT) {
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    }
    else {
      return `${year}-${month}-${day} ${hours}:${minutes}`;
    }
  },
  /**
   * Load templates from the API
   */
  loadTemplates: async ({ page, limit, folderId } = {}) => {
    try {
      const params = {};
      if (limit) {
        params.limit = limit;
        params.offset = ((page || 1) - 1) * limit;
        if (folderId) {
          params.folder_id = folderId;
        } else {
          params.no_folder = true;
        }
      }
      const response = await axios.get('/api/email-templates', { params });
      if (response.data.success) {
        if (limit) {
          return { templates: response.data.templates, total: response.data.total, folderCounts: response.data.folderCounts || {} };
        }
        return response.data.templates;
      }
      throw new Error('Failed to load templates');
    } catch (error) {
      console.error('Failed to load templates:', error);
      throw error;
    }
  },




  /**
   * Get template type options
   */
  getTemplateTypes: () => [
    'Cold Leads',
    'Warm Prospects',
    'Active Clients',
    'Follow-up',
    'Introduction',
    'Custom'
  ],

  /**
   * Validate template form data
   */
  validateTemplate: (formData) => {
    const errors = [];

    if (!formData.name?.trim()) {
      errors.push('Template name is required');
    }

    if (!formData.subject?.trim()) {
      errors.push('Subject line is required');
    }

    if (!formData.body?.trim()) {
      errors.push('Email body is required');
    }

    return errors;
  },


  /**
   * Get attachment file icon and formatted size
   */
  getAttachmentDisplay: (attachment) => {
    return {
      icon: fileAttachmentUtils.getFileIcon(attachment.mimeType),
      size: fileAttachmentUtils.formatFileSize(attachment.size),
      name: attachment.originalName || attachment.filename
    };
  }
};

/**
 * Custom hook for template management
 * Provides state and handlers for template operations
 */
export const useTemplateManager = (initialFormData = {}) => {

  const originalBodyRef = useRef(initialFormData.body);

  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    type: 'Cold Leads',
    subject: '',
    body: '',
    ...initialFormData
  });
  const [linkPreviews, setLinkPreviews] = useState({});
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [uploadingFile, setUploadingFile] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Detect URLs when body field changes
    if (name === 'body' && setLinkPreviews && setLoadingPreview && formData.linkPreviewToggle) {
      urlPreviewUtils.detectAndPreviewUrls(value, linkPreviews, setLinkPreviews, setLoadingPreview);
      return;
    }

    if (name == "linkPreviewToggle") {
      if (value) {
        if (setLinkPreviews && setLoadingPreview) {
          urlPreviewUtils.detectAndPreviewUrls(formData.body, linkPreviews, setLinkPreviews, setLoadingPreview);
        }
      }
      else {
        setLinkPreviews([]);
      }
      return;
    }
  };

  const initializeEditForm = (template) => {
    // Check if template has link previews to set the linkPreview checkbox
    let hasLinkPreviews = false;

    try {
      const previews = template.link_previews ? JSON.parse(template.link_previews) : {};
      hasLinkPreviews = Object.keys(previews).length > 0;
    } catch (error) {
      hasLinkPreviews = false;
    }
    setFormData({
      name: template.name,
      type: template.type,
      subject: template.subject,
      body: template.body,
      linkPreviewToggle: hasLinkPreviews
    });
    originalBodyRef.current = template.body;

    // Load link previews if they exist
    try {
      const previews = template.link_previews ? JSON.parse(template.link_previews) : {};
      setLinkPreviews(previews);
    } catch (error) {
      console.error('Failed to parse link previews:', error);
      setLinkPreviews({});
    }

    // Load attachments if they exist
    try {
      const attachments = template.attachments ? JSON.parse(template.attachments) : [];
      setAttachments(attachments);
    } catch (error) {
      console.error('Failed to parse attachments:', error);
      setAttachments([]);
    }
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    setUploadingFile(true);

    for (const file of files) {
      const validation = fileAttachmentUtils.validateFile(file);

      if (!validation.isValid) {
        alert(`File "${file.name}" is invalid:\n${validation.errors.join('\n')}`);
        continue;
      }

      // Check total size limit
      const sizeCheck = fileAttachmentUtils.isWithinSizeLimit([...attachments, { size: file.size }]);
      if (!sizeCheck.isValid) {
        alert(`Adding "${file.name}" would exceed the 25MB total attachment limit.\nCurrent total: ${fileAttachmentUtils.formatFileSize(sizeCheck.totalSize - file.size)}\nFile size: ${fileAttachmentUtils.formatFileSize(file.size)}`);
        continue;
      }

      try {
        const uploadedFile = await fileAttachmentUtils.uploadFile(file);
        const attachmentData = fileAttachmentUtils.createAttachmentData(uploadedFile);

        setAttachments(prev => [...prev, attachmentData]);
      } catch (error) {
        console.error('Failed to upload file:', error);
        alert(`Failed to upload "${file.name}": ${error.message}`);
      }
    }

    setUploadingFile(false);
    // Clear the input
    e.target.value = '';
  };

  const handleRemoveAttachment = async (attachmentId) => {
    try {
      await fileAttachmentUtils.deleteFile(attachmentId);
      setAttachments(prev => prev.filter(att => att.id !== attachmentId));
    } catch (error) {
      console.error('Failed to delete attachment:', error);
      alert(`Failed to remove attachment: ${error.message}`);
    }
  };

  const resetForm = (customInitialData = {}) => {
    const defaultFormData = {
      name: '',
      type: 'Cold Leads',
      subject: '',
      body: '',
      linkPreviewToggle: false,
      ...initialFormData,
      ...customInitialData
    };

    setFormData(defaultFormData);
    originalBodyRef.current = defaultFormData.body;
    setLinkPreviews({});
    setAttachments([]);
  };

  const createTemplate = async (overrides = {}) => {
    try {
      const dataWithExtras = {
        ...formData,
        ...overrides,
        link_previews: JSON.stringify(linkPreviews),
        attachments: JSON.stringify(attachments)
      };
      const response = await axios.post('/api/email-templates', dataWithExtras);

      if (response.data.success) {
        return response.data;
      }
      throw new Error(response.data.error || 'Failed to create template');
    } catch (error) {
      console.error('Failed to create template:', error);
      throw error;
    }
  };

  const updateTemplate = async (templateId, overrides = {}, fields = null) => {
    try {
      const dataWithExtras = {
        ...(fields || {
          ...formData,
          link_previews: JSON.stringify(linkPreviews),
          attachments: JSON.stringify(attachments)
        }),
        expected_body: originalBodyRef.current,
        ...overrides
      };
      const response = await axios.put(`/api/email-templates/${templateId}`, dataWithExtras);

      if (response.data.success) {
        originalBodyRef.current = response.data.template?.body ?? formData.body;
        return response.data;
      }
      throw new Error(response.data.error || 'Failed to update template');
    } catch (error) {
      console.error('Failed to update template:', error);
      if (error.response?.data?.code === 'TEMPLATE_CHANGED' && error.response.data.template) {
        initializeEditForm(error.response.data.template);
      }
      throw error;
    }
  };

  const deleteTemplate = async (templateId) => {
    try {
      const response = await axios.delete(`/api/email-templates/${templateId}`);

      if (response.data.success) {
        return response.data;
      }
      throw new Error(response.data.error || 'Failed to delete template');
    } catch (error) {
      console.error('Failed to delete template:', error);
      throw error;
    }
  };

  const loadTemplates = async (paginationOpts) => {
    try {
      const result = await templateUtils.loadTemplates(paginationOpts);
      if (paginationOpts?.limit) {
        setTemplates(result.templates);
        return result; // { templates, total, folderCounts }
      } else {
        setTemplates(result);
      }
    } catch (error) {
      console.error('Failed to load templates:', error);
    }
  };

  return {
    // State
    loading,
    formData,
    linkPreviews,
    loadingPreview,
    attachments,
    uploadingFile,
    templates,

    // Setters
    setLoading,
    setFormData,
    setLinkPreviews,
    setLoadingPreview,
    setAttachments,
    setUploadingFile,
    setTemplates,

    // Handlers
    handleInputChange,
    handleFileSelect,
    handleRemoveAttachment,
    resetForm,
    initializeEditForm,
    createTemplate,
    updateTemplate,
    deleteTemplate,
    loadTemplates,

    // Utils
    templateUtils
  };
};
